import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../../ui/dialog';
import { deleteMRIMachine } from '../../../services/apiService';
import { useToast } from '../../../hooks/use-toast';

interface DeleteRoomDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    machineId: string;
    machineName: string;
    equipmentCount: number;
    onDeleted: () => void;
}

export default function DeleteRoomDialog({
    open,
    onOpenChange,
    machineId,
    machineName,
    equipmentCount,
    onDeleted,
}: DeleteRoomDialogProps) {
    const { t } = useTranslation();
    const { toast } = useToast();
    const [isDeleting, setIsDeleting] = useState(false);

    const handleDelete = async () => {
        setIsDeleting(true);
        try {
            await deleteMRIMachine(machineId);
            toast({
                title: t('roomOverview.machineDetail.deleteSuccess'),
                description: t(
                    'roomOverview.machineDetail.deleteSuccessDescription',
                    { name: machineName }
                ),
            });
            onOpenChange(false);
            onDeleted();
        } catch (error) {
            console.error('Failed to delete machine:', error);
            toast({
                title: t('roomOverview.machineDetail.deleteError'),
                description: t(
                    'roomOverview.machineDetail.deleteErrorDescription'
                ),
                variant: 'destructive',
            });
        } finally {
            setIsDeleting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>
                        {t('roomOverview.machineDetail.deleteTitle')}
                    </DialogTitle>
                    <DialogDescription>
                        {t('roomOverview.machineDetail.deleteDescription', {
                            name: machineName,
                        })}
                        {equipmentCount > 0 && (
                            <span className="mt-2 block">
                                {t(
                                    'roomOverview.machineDetail.deleteEquipmentWarning',
                                    { count: equipmentCount }
                                )}
                            </span>
                        )}
                    </DialogDescription>
                </DialogHeader>
                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                    >
                        {t('roomOverview.machineDetail.cancel')}
                    </Button>
                    <Button
                        variant="destructive"
                        onClick={handleDelete}
                        disabled={isDeleting}
                    >
                        {isDeleting
                            ? t('roomOverview.machineDetail.deleting')
                            : t('roomOverview.machineDetail.delete')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
